import type { IOEPSeasonTimeline } from './timeline.js';

export type OEPEmailKey =
    | 'Welcome' | 'Matched' | 'DraftDue' | 'DraftsReady' | 'Training' | 'FeedbackReady'
    | 'NominationsOpen' | 'CommitteeOpen' | 'Winner' | 'NotSelected' | 'VolunteerThanks';

export interface OEPEmailDefinition {
    /** `MJ: Templates` row holding the HTML body (metadata/templates). */
    readonly TemplateName: string;
    /** Nunjucks subject, rendered with the same context as the body. */
    readonly Subject: string;
    /** When it is sent — shown in the staff email-templates panel (logic.js EMAILS `trigger`). */
    readonly Trigger: string;
}

/** No OEP email mints a sign-in invite (go-live D4): OEP people sign in with their AGU account and {{ Link }} is the /app/OEP URL.
 * Subjects verbatim from logic.js:81-113 with season/section/dates made variables. */
export const OEP_EMAILS: Readonly<Record<OEPEmailKey, OEPEmailDefinition>> = {
    Welcome: { TemplateName: 'OEP: Welcome', Subject: 'You are in. Welcome to Outstanding Emerging Presenters', Trigger: 'Gate: Close presenter application · presenters' },
    Matched: { TemplateName: 'OEP: Matched', Subject: 'You are matched to {{ MatchedPresenters }}', Trigger: 'Gate: Match reviewers to presenters · reviewers' },
    DraftDue: { TemplateName: 'OEP: DraftDue', Subject: 'Your OEP draft is due {{ Timeline.Dates.DraftDue.Label }}', Trigger: 'Gate: Open draft upload, and draft reminders · presenters' },
    DraftsReady: { TemplateName: 'OEP: DraftsReady', Subject: 'Your OEP drafts are ready to read', Trigger: 'Gate: Open the feedback window · reviewers' },
    Training: { TemplateName: 'OEP: Training', Subject: 'Your OEP training materials and office hours', Trigger: 'Action: Send training materials · nominators and committee' },
    FeedbackReady: { TemplateName: 'OEP: FeedbackReady', Subject: 'Your reviewer feedback is ready', Trigger: 'Gate: Release feedback to presenters · presenters' },
    NominationsOpen: { TemplateName: 'OEP: NominationsOpen', Subject: 'Nominating is open', Trigger: 'Gate: Open nominations · nominators' },
    CommitteeOpen: { TemplateName: 'OEP: CommitteeOpen', Subject: 'Nominations are in. Your section selects winners by {{ Timeline.Dates.SelectionDue.Label }}', Trigger: 'Gate: Close nominations, open committee view · committee' },
    Winner: { TemplateName: 'OEP: Winner', Subject: 'You are an Outstanding Emerging Presenter', Trigger: 'Gate: Announce outcomes · winning presenters' },
    NotSelected: { TemplateName: 'OEP: NotSelected', Subject: 'Thank you for presenting at {{ SeasonCode }}', Trigger: 'Gate: Announce outcomes · other presenters' },
    VolunteerThanks: { TemplateName: 'OEP: VolunteerThanks', Subject: 'Thank you for volunteering with OEP', Trigger: 'Gate: Announce outcomes · reviewers and nominators' },
};

/** Every variable a subject or body may reference (contracts.spec.ts enforces this). */
export interface IOEPEmailContext {
    FirstName: string;
    /** The OEP app URL (OEPAppUrl: OEP_APP_ORIGIN + /app/OEP); sign-in is the AGU account, never a magic link. */
    Link: string;
    SeasonCode: string;
    SeasonName: string;
    Timeline: IOEPSeasonTimeline;
    AssignmentCount: number;
    /** "1 OEP presenter" / "N OEP presenters" (OEPMatchedPresenters) — the Matched subject and in-app title. */
    MatchedPresenters: string;
    Sections: string;
    SectionName: string;
    WinnersPerSection: number;
}

/** The Matched subject's count phrase, singular for one assignment (the subject is also the in-app title). */
export function OEPMatchedPresenters(count: number): string {
    return count === 1 ? '1 OEP presenter' : `${count} OEP presenters`;
}
